import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  Button, 
  CircularProgress, 
  Alert, 
  AlertTitle 
} from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { API, graphqlOperation } from 'aws-amplify';
import { listJobDefinitions } from 'graphql/queries';
import { JobsList } from './JobsList';
import { CreateJobModal } from './CreateJobModal';
import { EditJobModal } from './EditJobModal';
import { DeleteConfirmationModal } from './DeleteConfirmationModal';

export const JobDefinitionsTab = () => {
  // State management
  const [jobDefinitions, setJobDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  // Fetch job definitions
  const fetchJobDefinitions = async () => {
    setLoading(true);
    setError(null);
    try {
      // For Phase 0, we're using a simple query without much filtering
      // This can be enhanced in future phases
      const response = await API.graphql(graphqlOperation(listJobDefinitions, {
        filter: {
          dataSource: { eq: "toast-pos" } // Focus on Toast for Phase 0
        },
        limit: 100
      }));

      console.log('fetchJobDefinitions response', response)

      const items = response.data.listJobDefinitions.items || []
      console.log('items', items)
      setJobDefinitions(items);
    } catch (err) {
      console.error('Error fetching job definitions:', err);
      setError('Failed to load job definitions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Get job by ID from existing state
  const getJobById = (jobDefinitionId) => {
    const job = jobDefinitions.find(job => job.jobDefinitionId === jobDefinitionId);
    if (!job) {
      console.error(`Job with ID ${jobDefinitionId} not found in state`);
    }
    return job;
  };

  // Initial load
  useEffect(() => {
    fetchJobDefinitions();
  }, []);

  useEffect(() => {
    console.log('JobDefinitionsTab - jobDefinitions updated', jobDefinitions)
  }, [jobDefinitions]);

  // Handle row actions
  const handleEdit = (job) => {
    console.log('handleEdit - job received:', job);
    
    // Check if we received a job object or just an ID
    if (typeof job === 'string') {
      // We have an ID, not a complete job object
      const completeJob = getJobById(job);
      if (completeJob) {
        console.log('Setting selected job with data from state:', completeJob);
        setSelectedJob(completeJob);
        setShowEditModal(true);
      } else {
        setError(`Could not find job with ID ${job} for editing.`);
      }
    } else if (job && job.jobDefinitionId && !job.dataTimeConfig) {
      // We have a partial job object, get the full one from state
      const completeJob = getJobById(job.jobDefinitionId);
      if (completeJob) {
        console.log('Setting selected job with data from state:', completeJob);
        setSelectedJob(completeJob);
        setShowEditModal(true);
      } else {
        setError(`Could not find job with ID ${job.jobDefinitionId} for editing.`);
      }
    } else {
      // We have a complete job object
      console.log('Setting selected job with provided data:', job);
      setSelectedJob(job);
      setShowEditModal(true);
    }
  };

  const handleDelete = (job) => {
    // Check if we received a job object or just an ID
    if (typeof job === 'string') {
      // We have an ID, not a complete job object
      const completeJob = getJobById(job);
      if (completeJob) {
        console.log('Setting selected job with data from state:', completeJob);
        setSelectedJob(completeJob);
        setShowDeleteModal(true);
      } else {
        setError(`Could not find job with ID ${job} for deleting.`);
      }
    } else if (job && job.jobDefinitionId && !job.dataTimeConfig) {
      // We have a partial job object, get the full one from state
      const completeJob = getJobById(job.jobDefinitionId);
      if (completeJob) {
        console.log('Setting selected job with data from state:', completeJob);
        setSelectedJob(completeJob);
        setShowDeleteModal(true);
      } else {
        setError(`Could not find job with ID ${job.jobDefinitionId} for editing.`);
      }
    } else {
      // We have a complete job object
      console.log('Setting selected job with provided data:', job);
      setSelectedJob(job);
      setShowDeleteModal(true);
    }
  };

  const handleToggleStatus = (job) => {
    // Placeholder for toggle status functionality
    console.log('Toggle status:', job);
  };

  const handleClone = (job) => {
    console.log('job', job)
    // Create a new job object based on the selected one
    const clonedJob = {
      ...job,
      name: `${job.name} (Clone)`,
      // Reset certain fields that should be unique or reset for a clone
      jobDefinitionId: undefined,
      status: 'ACTIVE',
      createdBy: undefined,
      createdAt: undefined,
      lastModified: undefined
    };
    setSelectedJob(clonedJob);
    setShowCreateModal(true);
  };

  const handleRunNow = (job) => {
    // Placeholder for run now functionality
    console.log('Run now:', job);
  
  }

  // Modal close handlers
  const handleModalClose = (refresh = false) => {
    console.log('JobDefinitionTab - Modal Closing, refresh =', refresh)
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setSelectedJob(null);
    
    if (refresh) {
      console.log('JobDefinitionTab - Refreshing job definitions')
      fetchJobDefinitions();
    }
  };

  return (
    <div className="space-y-4">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 600 }}>Job Definitions</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button 
            variant="outlined" 
            size="small"
            onClick={fetchJobDefinitions}
            disabled={loading}
            startIcon={<RefreshIcon />}
            sx={{
              borderColor: '#757575',
              color: '#424242',
              '&:hover': {
                borderColor: '#424242',
                backgroundColor: 'rgba(66, 66, 66, 0.04)',
              },
              textTransform: 'none',
            }}
          >
            Refresh
          </Button>
          <Button 
            variant="contained" 
            size="small"
            onClick={() => {
              setSelectedJob(null);
              setShowCreateModal(true);
            }}
            startIcon={<AddIcon />}
            sx={{
              backgroundColor: '#e91e63', // Pink/magenta color like tab icons
              color: '#fff',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#c2185b', // Darker shade for hover
              },
              textTransform: 'none', // Keeps original case instead of uppercase
            }}
          >
            Create Job
          </Button>
        </div>
      </div>

      <Card>
        <CardContent style={{ padding: 0 }}>
          {error && (
            <Alert severity="error" style={{ margin: '16px' }}>
              <AlertTitle>Error</AlertTitle>
              {error}
            </Alert>
          )}
          
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '32px' }}>
              <CircularProgress size={24} />
              <span style={{ marginLeft: '8px' }}>Loading job definitions...</span>
            </div>
          ) : (
            <JobsList
              jobs={jobDefinitions}
              onEdit={handleEdit}
              onRunNow={handleRunNow}
              onToggleStatus={handleToggleStatus}
              onDelete={handleDelete} 
            />
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      {showCreateModal && (
        <CreateJobModal 
          initialData={selectedJob} 
          onClose={handleModalClose} 
        />
      )}
      
      {showEditModal && selectedJob && (
        <EditJobModal 
          job={selectedJob} 
          onClose={handleModalClose} 
        />
      )}
      
      {showDeleteModal && selectedJob && (
        <DeleteConfirmationModal 
          job={selectedJob} 
          onClose={handleModalClose} 
        />
      )}
    </div>
  );
};